import { Mark, Schema } from "prosemirror-model";
import { EditorState, Command } from "prosemirror-state";
import { useCallback, useMemo } from "react";
import { addMark } from "../commands/addMark";
import { removeMark } from "../commands/removeMark";
import { ColorMarkAttributes } from "../schema/marks/color";
import { getUnanimousValueFromSelection } from "../utils";
import { hasMarks } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";

export type ColorPickerToolHandler = {
	isSupportedBySchema: true;
	color: string | undefined;
	colorChangeHandler(newValue: string | undefined, isSignificant: boolean): void;
} | {
	isSupportedBySchema: false;
};

export function useColorPickerTool(
	editorState: EditorState,
	applyCommand: (command: Command, isSignificant: boolean) => void
): ColorPickerToolHandler {
	const getValueHandler: CallbackWithSchemaResult<
		[editorState: EditorState],
		string | undefined
	> = useExtendWithSchema(
		editorState.schema,
		(schema) => hasMarks(schema, ["color"]),
		getColorFromSelection
	);

	const changeHandler: CallbackWithSchemaResult<
		[newValue: string | undefined, isSignificant: boolean],
		void
	> = useExtendWithSchema(
		editorState.schema,
		(schema) => hasMarks(schema, ["color"]),
		useCallback(
			(
				schema: Schema<never, "color">,
				newValue: string | undefined,
				isSignificant: boolean
			): void => {
				if (!newValue) {
					applyCommand(
						removeMark(schema.marks.color),
						isSignificant
					);
				} else {
					const attrs: ColorMarkAttributes = {
						color: newValue
					};

					applyCommand(
						addMark(
							schema.marks.color
								.create(attrs)
						),
						isSignificant
					);
				}
			},
			[applyCommand]
		)
	);

	return useMemo(
		(): ColorPickerToolHandler => {
			if (!getValueHandler.isSupportedBySchema || !changeHandler.isSupportedBySchema) {
				return {
					isSupportedBySchema: false
				};
			}

			return {
				isSupportedBySchema: true,
				color: getValueHandler(editorState),
				colorChangeHandler: changeHandler
			};
		},
		[
			changeHandler,
			editorState,
			getValueHandler
		]
	);
}

function getColorFromSelection(
	schema: Schema<never, "color">,
	editorState: EditorState
): string | undefined {
	return getUnanimousValueFromSelection<string | undefined>(
		editorState,
		schema.marks.color,
		getColorFromMark,
		undefined,
		areEqual
	);
}

function getColorFromMark(mark: Mark): string {
	const attrs = mark.attrs as ColorMarkAttributes;
	return attrs.color;
}

function areEqual(value1: string | undefined, value2: string | undefined): boolean {
	return value1 === value2;
}
